/**
 * RainShelterModule — the ROOF MAP's producer (render wave 1, 2026-09-17), since the shader gaps program's round 3
 * (weather surfaces, 2026-09-30) the WIND'S SHELTER, the GROUND'S MEMORY and the ONE WET CLOCK's levers.
 *
 * THE ROOF (the atlas's upper region, `ROOF_VIEWPORT`): an orthographic camera renders everything on
 * RAIN_SHELTER_LAYER with an override material that writes the fragment's ABSOLUTE height (render y − scene.position.y)
 * and alpha 1. The camera is SHEARED along the precipitation (`shelterViewMatrix`: a drop travels along (sx, −1, sz),
 * `precipShear` — the same lean the emitters use) so every rain LINE is one texel and the depth test keeps the
 * highest occluder on it; with no wind the camera is the old one, straight down. The shear EASES toward the wind
 * (`easeShear`, `SHEAR_EASE_PER_S`) and is HELD while nothing falls (the drying ground keeps the patch the rain left);
 * a swing past `SHEAR_REBAKE_EPS` re-bakes at once (throttled). The region's "clear" is a quad writing (0,0,0,0) over
 * its own viewport and a depth-only clear — a colour clear is a WebGPU loadOp over the whole attachment and would wipe
 * the deformation region below.
 *
 * WHAT IS A ROOF, ADDED: parked ships (proxy discs from the vehicle module's public `parkedShips()`, layer 7 ONLY, so
 * the main camera never draws them) and the player's own ride while it idles (its meshes join the layer for the bake).
 *
 * CADENCE: bake on a re-centre (the player 24 m from the window's middle), on the first frame a world is up, every
 * SHELTER_REBAKE_S while it rains, snows or the ground is still drying, and on a shear swing; never otherwise. Off
 * (every point exposed) when the tier flag is off, in space, on no world, in a non-building interior and while XR
 * presents. THE ROOF-INDOORS LAW (2026-09-22): inside a BUILDING the map stays ON and re-bakes at once.
 *
 * THE WEBGL2 DROPS: the CPU adapter cannot run the GPU size gate, so after a bake on a CPU-drop backend the roof
 * region is read back (one readback in flight; `roofReadRect` knows each backend's row order) into the CPU grid
 * `gateCpuEmitters` tests every live drop against.
 *
 * THE GROUND'S MEMORY (the atlas's lower region, `DEFORM_VIEWPORT`): the camera-following deformation field
 * (`lib/deform_field.ts`, the engine heightmap) is drawn into it by a tiny scene of its own (a plane that samples the
 * field's DataTexture by position) whenever the field changed, ≤ 1 in 150 ms. Fed by every stride the footprint module
 * computes (`deformStride`) and by the ride's cushion over snow; filled by fresh snowfall, settled by rain.
 *
 * Levers: `__shelter()` (the whole state; `setShear`, `sampleAt`, `exposureAt`, `debugRoof`, `bakeNow`, `grid`),
 * `__rainShelter()` (the old name, kept for its probe), `__deform()` (the field: `stamp`, `depthAt`, `clear`, `walkTrail`)
 * and `__wet()` (the clock: levels, `pin(level)`, `freeze(on)`, `stepHours(h, rainMmH)`).
 */
import * as THREE from 'three/webgpu';
import type { App, IModule } from 'enginex';
import { uniform, vec4, vec2, positionWorld, texture as tslTexture, float } from 'three/tsl';
import { Phase, makeSystem } from '../engine-compat.js';
import { atlas } from '../state.js';
import { activePreset } from '../lib/quality_tiers.js';
import { getWetness, getWetLevels } from '../lib/wetness.js';
import { enterables } from '../lib/building_interiors.js';
import {
  RAIN_SHELTER_LAYER, SHELTER_REGION_M, SHELTER_CAM_ABOVE_M, SHELTER_CAM_FAR_M, SHELTER_REBAKE_S, SHELTER_RES,
  ensureShelterTarget, shelterBounds, shelterEnabled, shelterBoundsRender, shelterNeedsRecenter, shelterTexelUv, shelterExposureCpu, shelterActiveIndoors,
  shelterShear, shelterViewMatrix, shelterShearedUv, precipShear, easeShear, SHEAR_REBAKE_EPS, roofReadRect, roofRowToMapV,
  gridFromReadback, setShelterCpuGrid, shelterCpuGrid, shelterExposureFromGrid, cpuGateStats, ROOF_VIEWPORT, DEFORM_VIEWPORT, seatViewMatrix,
  deformBounds, deformEnabled,
} from '../lib/rain_shelter.js';
import { DeformField, DEFORM_VIEW_M, DEFORM_CELL_M, printStamp, hoverStamp, snowFillM, rainSettleM, deformRuntime, deformOn } from '../lib/deform_field.js';
import { wetClock, rainRateMmH, WET_CLASSES, stepWet, wetLevels, type WetClass } from '../lib/wet_state.js';
import { wetDriveStats, wetDriveControl } from '../lib/wet_drive.js';
import { wetLayerStats, wetSurfacesMode } from '../lib/wet_layer.js';
import { materialFinishStats } from '../lib/material_finish.js';

interface WeatherLike {
  getPrecip?: () => { kind: string; density: number };
  wind?: () => { x: number; z: number; strength: number; kind: string };
  snowLevel?: number;
}
interface VehicleLike { parkedShips?: () => Array<{ x: number; y: number; z: number; top: number; half: number }>; rideObject?: () => THREE.Object3D | null }
interface EnvLike { groundAt?: (x: number, z: number) => number }

/** A shear swing re-bakes at most this often (s). */
const SHEAR_REBAKE_MIN_S = 0.5;
/** The deformation region is re-drawn at most this often (s). */
const DEFORM_BAKE_MIN_S = 0.15;
/** The ride joins the roof layer only while it idles slower than this (m/s) — a moving hull would leave a stale patch. */
const RIDE_ROOF_MAX_SPEED = 3;
/** At most this many parked-ship proxies. */
const MAX_SHIP_PROXIES = 12;

export class RainShelterModule implements IModule {
  readonly name = 'RainShelter';
  private app!: App;
  private rt!: THREE.RenderTarget;
  private cam!: THREE.OrthographicCamera;
  private bakeMat!: THREE.MeshBasicNodeMaterial;
  private clearQuad!: THREE.QuadMesh;
  private readonly uSceneY = uniform(0);
  private readonly view = new THREE.Matrix4();
  private centerAbs: [number, number] = [Number.NaN, Number.NaN];
  private bakes = 0;
  private lastBakeMs = 0;
  private lastOn = false;
  private lastInterior: string | null = null;
  private readonly debugRoofs: THREE.Mesh[] = [];
  private readonly _clear = new THREE.Color();
  // ── the wind's shelter ──
  private shear: [number, number] = [0, 0];
  private shearTarget: [number, number] = [0, 0];
  private shearBaked: [number, number] = [0, 0];
  private yRefBaked = 0;
  private shearOverride: [number, number] | null = null;
  private lastTickMs = 0;
  private readbackBusy = false;
  private readbacks = 0;
  private readbackAgain = false;
  private readbackError = '';
  // ── ships on the roof ──
  private readonly shipGroup = new THREE.Group();
  private readonly shipProxies: THREE.Mesh[] = [];
  private rideOnRoof = false;
  private lastRidePos: [number, number] | null = null;
  private rideSpeed = 0;
  // ── the ground's memory ──
  readonly field = new DeformField();
  private deformScene!: THREE.Scene;
  private deformCam!: THREE.OrthographicCamera;
  private deformMat!: THREE.MeshBasicNodeMaterial;
  private deformTexNode: ReturnType<typeof tslTexture> | null = null;
  private deformBakedVersion = -1;
  private deformBakedMin: [number, number] = [0, 0];
  private deformBakes = 0;
  private lastDeformBakeMs = 0;
  private fillAcc = 0;
  private lastHover: [number, number] | null = null;
  private deformWorld: string | null = null;

  init(app: App): void {
    this.app = app;
    this.rt = ensureShelterTarget();
    const h = SHELTER_REGION_M / 2;
    this.cam = new THREE.OrthographicCamera(-h, h, h, -h, 0, SHELTER_CAM_FAR_M);
    this.cam.matrixAutoUpdate = false; // THE SHEAR: the view is seated from shelterViewMatrix at every bake (seatViewMatrix)
    this.cam.matrixWorldAutoUpdate = false;
    this.cam.layers.set(RAIN_SHELTER_LAYER);
    this.bakeMat = new THREE.MeshBasicNodeMaterial({ side: THREE.DoubleSide });
    this.bakeMat.colorNode = vec4(positionWorld.y.sub(this.uSceneY as never), 0, 0, 1);
    this.bakeMat.fog = false;
    this.bakeMat.name = 'rain-shelter-bake';
    // the roof region's "clear": (0, 0, 0, 0) — NoBlending keeps the zero alpha (an opaque NormalBlending material forces 1)
    const clearMat = new THREE.MeshBasicNodeMaterial();
    clearMat.colorNode = vec4(0, 0, 0, 0);
    clearMat.blending = THREE.NoBlending;
    clearMat.depthTest = false; clearMat.depthWrite = false; clearMat.fog = false;
    clearMat.name = 'rain-shelter-clear';
    this.clearQuad = new THREE.QuadMesh(clearMat);
    this.buildDeformScene();
    this.shipGroup.name = 'rain-shelter-ship-proxies';
    app.world.scene.add(this.shipGroup);
    deformRuntime.field = this.field;
    app.world.addSystem(makeSystem('AtlasRainShelter', Phase.RenderSub, 9, () => this.tick()));
    const lever = (): Record<string, unknown> => this.state();
    (globalThis as unknown as { __rainShelter?: () => unknown }).__rainShelter = lever;
    (globalThis as unknown as { __shelter?: () => unknown }).__shelter = lever;
    (globalThis as unknown as { __deform?: () => unknown }).__deform = () => this.deformState();
    (globalThis as unknown as { __wet?: () => unknown }).__wet = () => this.wetState();
  }

  // ── levers ──

  private state(): Record<string, unknown> {
    const g = shelterCpuGrid();
    return {
      enabled: (shelterEnabled as unknown as { value: number }).value === 1,
      flag: !!activePreset().rainShelter,
      center: [this.centerAbs[0], this.centerAbs[1]],
      size: SHELTER_REGION_M,
      res: SHELTER_RES,
      bakes: this.bakes,
      lastBakeMs: this.lastBakeMs,
      interior: this.lastInterior,
      bounds: shelterBounds.toArray(),
      shear: { baked: [...this.shearBaked], eased: [...this.shear], target: [...this.shearTarget], override: this.shearOverride, yRef: this.yRefBaked },
      layerMeshes: this.countLayerMeshes(),
      debugRoofs: this.debugRoofs.length,
      ships: this.shipProxies.filter((m) => m.visible).length,
      rideOnRoof: this.rideOnRoof,
      cpuGrid: g ? { bakedAt: g.bakedAt, ageMs: performance.now() - g.bakedAt, sx: g.sx, sz: g.sz, yRef: g.yRef, minX: g.minX, minZ: g.minZ } : null,
      readbacks: this.readbacks, readbackError: this.readbackError,
      cpuGate: { ...cpuGateStats },
      sampleAt: (x: number, z: number) => this.sampleAt(x, z),
      exposureAt: (x: number, y: number, z: number) => this.exposureAt(x, y, z),
      gridExposureAt: (x: number, y: number, z: number) => shelterExposureFromGrid(shelterCpuGrid(), x, y, z),
      debugRoof: (x: number, z: number, y: number, size = 12, sizeZ?: number) => this.debugRoof(x, z, y, size, sizeZ),
      clearDebugRoofs: () => this.clearDebugRoofs(),
      /** Pin the shear (probes: [sx, sz]) or give it back to the weather (null); re-bakes at once. */
      setShear: (s: [number, number] | null) => { this.shearOverride = s ? [s[0], s[1]] : null; if (s) { this.shear = [s[0], s[1]]; } this.bakeNow(); return this.shearBaked; },
      bakeNow: () => this.bakeNow(),
      readGrid: () => this.readGrid(true),
    };
  }

  private bakeNow(): void {
    this.centerAbs = [atlas.localX, atlas.localZ];
    this.bake();
  }

  private deformState(): Record<string, unknown> {
    const b = this.field.bounds;
    return {
      on: deformOn(),
      live: (deformEnabled as unknown as { value: number }).value === 1,
      version: this.field.version, bakedVersion: this.deformBakedVersion, bakes: this.deformBakes,
      stamps: this.field.stamps, scrolls: this.field.scrolls,
      window: { minX: b.minX, minZ: b.minZ, maxX: b.maxX, maxZ: b.maxZ },
      boundsUniform: deformBounds.toArray(),
      depthAt: (x: number, z: number) => this.field.depthAt(x, z),
      stamp: (x: number, z: number, yaw = 0, depth = 0.08) => { this.field.follow(atlas.localX, atlas.localZ); this.field.stamp(printStamp(x, z, yaw, depth)); return this.field.version; },
      /** A trail of prints (probes): n strides from (x, z) along yaw, alternating feet. */
      walkTrail: (x: number, z: number, yaw: number, n = 16, depth = 0.08) => {
        this.field.follow(atlas.localX, atlas.localZ);
        const fx = Math.sin(yaw), fz = Math.cos(yaw);
        for (let i = 0; i < n; i++) {
          const side = i % 2 ? 1 : -1, s = i * 0.72;
          this.field.stamp(printStamp(x + fx * s + fz * 0.11 * side, z + fz * s - fx * 0.11 * side, yaw, depth));
        }
        return this.field.version;
      },
      clear: () => { this.field.reset(); return true; },
    };
  }

  private wetState(): Record<string, unknown> {
    const out: Record<string, unknown> = {
      mode: wetSurfacesMode(), frozen: wetDriveControl.frozen,
      ground: getWetLevels(), drive: { ...wetDriveStats }, props: { ...wetLayerStats, byClass: { ...wetLayerStats.byClass } },
      finishWet: materialFinishStats.wet, floor: wetClock.floor01, elapsedS: wetClock.elapsedS, inputs: wetClock.lastInputs,
      /** Pin every class to a level (0 dry … 1 soaked); freeze(true) holds it (probes). */
      pin: (level: number) => { wetClock.pin(level); return true; },
      freeze: (on: boolean) => { wetDriveControl.frozen = !!on; return wetDriveControl.frozen; },
      /** Advance the clock `h` GAME hours at `rainMmH` with the last inputs (probes; the drive pushes the result). */
      stepHours: (h: number, rainMmH = 0) => {
        const inp = wetClock.lastInputs ?? { rainMmH: 0, tempC: 16, rh: 0.6, windMs: 3, sun: 0.5, floor01: 0 };
        const dt = (h * 3600) / wetClock.timeScale;
        for (const c of WET_CLASSES) stepWet(wetClock.state[c], c, { ...inp, rainMmH }, dt, wetClock.timeScale);
        return this.levels();
      },
      rainRate: (kind: string, density: number) => rainRateMmH(kind, density),
    };
    out.levels = this.levels();
    return out;
  }

  private levels(): Record<string, unknown> {
    const o: Record<string, unknown> = {};
    for (const c of WET_CLASSES) o[c] = wetLevels(wetClock.state[c as WetClass], c as WetClass);
    return o;
  }

  private countLayerMeshes(): number {
    let n = 0;
    this.app.world.scene.traverse((o) => { if ((o as THREE.Mesh).isMesh && o.layers.isEnabled(RAIN_SHELTER_LAYER)) n++; });
    return n;
  }

  // ── the tick ──

  private tick(): void {
    const nowMs = performance.now();
    const dt = this.lastTickMs ? Math.min(1, (nowMs - this.lastTickMs) / 1000) : 0;
    this.lastTickMs = nowMs;
    const p = activePreset();
    const native = this.app.renderer.native as unknown as { xr?: { isPresenting?: boolean } };
    const inBuilding = atlas.interiorId !== null && enterables.has(atlas.interiorId);
    const onWorld = !atlas.spaceFlight && atlas.currentWorldId != null && !atlas.loadingGate && !native.xr?.isPresenting;
    const on = !!p.rainShelter && onWorld && shelterActiveIndoors(atlas.interiorId, inBuilding);
    (shelterEnabled as unknown as { value: number }).value = on ? 1 : 0;
    const interiorChanged = this.lastInterior !== atlas.interiorId;
    this.lastInterior = atlas.interiorId;
    const weather = this.app.getModule('Weather') as WeatherLike | undefined;
    this.tickRide(dt);
    this.tickDeform(dt, onWorld && !!p.rainShelter, weather);
    if (!on) { if (this.lastOn) setShelterCpuGrid(null); this.lastOn = false; return; }
    const sp = this.app.world.scene.position;
    const px = atlas.localX, pz = atlas.localZ;
    // THE WIND'S SHELTER: the target follows the falling precipitation; nothing falling → HOLD (the drying patch stays)
    const w = weather?.wind?.();
    if (this.shearOverride) this.shearTarget = [this.shearOverride[0], this.shearOverride[1]];
    else if (w && (w.kind === 'rain' || w.kind === 'snow')) this.shearTarget = precipShear(w.kind, w.x, w.z, w.strength);
    this.shear = this.shearOverride ? [...this.shearOverride] as [number, number] : easeShear(this.shear, this.shearTarget, dt);
    const swing = Math.hypot(this.shear[0] - this.shearBaked[0], this.shear[1] - this.shearBaked[1]);
    // A door crossing swaps what stands over the player (exterior ↔ shell) — bake now, not 24 m later.
    if (!this.lastOn || interiorChanged || Number.isNaN(this.centerAbs[0]) || shelterNeedsRecenter(this.centerAbs[0], this.centerAbs[1], px, pz)) {
      this.centerAbs = [px, pz];
      this.bake();
    } else if (swing > SHEAR_REBAKE_EPS && nowMs - this.lastBakeMs > SHEAR_REBAKE_MIN_S * 1000) {
      this.bake();
    } else if (nowMs - this.lastBakeMs > SHELTER_REBAKE_S * 1000) {
      const pr = weather?.getPrecip?.() ?? { kind: 'none', density: 0 };
      if ((pr.kind !== 'none' && pr.density > 0) || getWetness() > 0.001) this.bake();
    }
    this.lastOn = true;
    // The bounds ride in RENDER space and follow every rebase (origin = −scene.position).
    const [minX, minZ, size] = shelterBoundsRender(this.centerAbs[0], this.centerAbs[1], -sp.x, -sp.z, SHELTER_REGION_M);
    shelterBounds.set(minX, minZ, size, sp.y);
  }

  /** The player's ride: its idle speed decides whether it roofs the ground under it. */
  private tickRide(dt: number): void {
    const riding = atlas.hoverRide || atlas.skyFlight;
    if (!riding) { this.lastRidePos = null; this.rideSpeed = 0; return; }
    const x = atlas.localX, z = atlas.localZ;
    if (this.lastRidePos && dt > 0) this.rideSpeed = Math.hypot(x - this.lastRidePos[0], z - this.lastRidePos[1]) / dt;
    this.lastRidePos = [x, z];
  }

  /** One sheared top-down render of the roof layer into the atlas's roof region. */
  private bake(): void {
    const renderer = this.app.renderer as unknown as { native: THREE.WebGPURenderer; paused?: boolean; isWebGPU?: boolean };
    if (renderer.paused) return;
    const r = renderer.native;
    const scene = this.app.world.scene;
    const sp = scene.position;
    const [cx, cz] = this.centerAbs;
    const yRefAbs = atlas.localY;
    const [sx, sz] = this.shear;
    // the camera: shelterViewMatrix in RENDER space (its inverse is the camera's world matrix)
    this.view.fromArray(shelterViewMatrix(cx + sp.x, cz + sp.z, yRefAbs + sp.y, yRefAbs + sp.y + SHELTER_CAM_ABOVE_M, sx, sz));
    seatViewMatrix(this.cam, this.view); // THE CAMERA-SCALE LAW: updateMatrixWorld would drop the shear
    (this.uSceneY as unknown as { value: number }).value = sp.y;
    this.syncShipProxies();
    const ride = this.rideRoofObject();
    const rideLayers: Array<[THREE.Object3D, number]> = [];
    if (ride) ride.traverse((o) => { rideLayers.push([o, o.layers.mask]); o.layers.enable(RAIN_SHELTER_LAYER); });
    this.rideOnRoof = !!ride;
    const prevRT = r.getRenderTarget();
    const prevOverride = scene.overrideMaterial;
    const prevAuto = r.autoClear;
    const prevBg = scene.background, prevBgNode = (scene as { backgroundNode?: unknown }).backgroundNode ?? null;
    r.getClearColor(this._clear as unknown as never); const prevAlpha = r.getClearAlpha();
    const rt = this.rt;
    const prevVp = rt.viewport.clone();
    try {
      // The sky's background node is not layered and would paint a "roof" over the whole map.
      scene.background = null; (scene as { backgroundNode?: unknown }).backgroundNode = null;
      r.setRenderTarget(rt);
      r.autoClear = false;
      rt.viewport.set(...ROOF_VIEWPORT); // never equal to a canvas (the gutter — THE VIEWPORT QUIRK); no scissor needed or honoured
      this.clearQuad.render(r as never); // (0, 0, 0, 0) over the roof region only
      r.clearDepth();
      scene.overrideMaterial = this.bakeMat;
      r.render(scene, this.cam);
    } finally {
      scene.overrideMaterial = prevOverride;
      scene.background = prevBg; (scene as { backgroundNode?: unknown }).backgroundNode = prevBgNode;
      rt.viewport.copy(prevVp);
      r.autoClear = prevAuto;
      r.setClearColor(this._clear, prevAlpha);
      r.setRenderTarget(prevRT);
      for (const [o, mask] of rideLayers) o.layers.mask = mask;
    }
    this.shearBaked = [sx, sz];
    this.yRefBaked = yRefAbs;
    shelterShear.set(sx, sz, yRefAbs, 0);
    this.bakes++;
    this.lastBakeMs = performance.now();
    // the WebGL2 CPU drops need their copy of the roof
    if (!renderer.isWebGPU) this.readGrid(false);
  }

  /** Read the roof region back into the CPU grid (one readback in flight). */
  private async readGrid(force: boolean): Promise<boolean> {
    // A bake landing while a readback is in flight must still be read: remember it and read again when that one ends.
    if (this.readbackBusy && !force) { this.readbackAgain = true; return false; }
    this.readbackBusy = true;
    this.readbackAgain = false;
    const meta = { minX: this.centerAbs[0] - SHELTER_REGION_M / 2, minZ: this.centerAbs[1] - SHELTER_REGION_M / 2, size: SHELTER_REGION_M, sx: this.shearBaked[0], sz: this.shearBaked[1], yRef: this.yRefBaked, bakedAt: this.lastBakeMs };
    try {
      const webgpu = !!(this.app.renderer as unknown as { isWebGPU?: boolean }).isWebGPU;
      const [x, y, w, h] = roofReadRect(webgpu);
      const rr = this.app.renderer.native as unknown as { readRenderTargetPixelsAsync: (rt: THREE.RenderTarget, x: number, y: number, w: number, h: number) => Promise<ArrayLike<number>> };
      const buf = await rr.readRenderTargetPixelsAsync(this.rt, x, y, w, h);
      const half = buf instanceof Uint16Array;
      setShelterCpuGrid(gridFromReadback(buf, half, webgpu, meta, (v) => THREE.DataUtils.fromHalfFloat(v)));
      this.readbacks++;
      return true;
    } catch (e) {
      this.readbackError = String(e).slice(0, 160);
      return false;
    } finally {
      this.readbackBusy = false;
      if (this.readbackAgain) void this.readGrid(false);
    }
  }

  // ── ships on the roof ──

  private syncShipProxies(): void {
    const veh = this.app.getModule('AtlasVehicle') as VehicleLike | undefined;
    const ships = (atlas.currentWorldId !== null && atlas.currentWorldId === atlas.commonsWorldId ? veh?.parkedShips?.() : null) ?? [];
    const near = ships
      .map((s) => ({ s, d: Math.hypot(s.x - this.centerAbs[0], s.z - this.centerAbs[1]) }))
      .filter((o) => o.d < SHELTER_REGION_M * 1.5)
      .sort((a, b) => a.d - b.d)
      .slice(0, MAX_SHIP_PROXIES);
    while (this.shipProxies.length < near.length) {
      const m = new THREE.Mesh(new THREE.CylinderGeometry(1, 1, 0.4, 24), this.bakeMat);
      m.layers.set(RAIN_SHELTER_LAYER); // ONLY the bake sees it
      m.frustumCulled = false;
      this.shipGroup.add(m);
      this.shipProxies.push(m);
    }
    this.shipProxies.forEach((m, i) => {
      const o = near[i];
      m.visible = !!o;
      if (!o) return;
      m.position.set(o.s.x, o.s.top, o.s.z); // ABSOLUTE (a scene child)
      m.scale.set(o.s.half, 1, o.s.half);
      m.updateMatrixWorld(true);
    });
  }

  private rideRoofObject(): THREE.Object3D | null {
    if (!(atlas.hoverRide || atlas.skyFlight) || this.rideSpeed > RIDE_ROOF_MAX_SPEED) return null;
    const veh = this.app.getModule('AtlasVehicle') as VehicleLike | undefined;
    return veh?.rideObject?.() ?? null;
  }

  // ── the ground's memory ──

  private buildDeformScene(): void {
    this.deformScene = new THREE.Scene();
    const hh = DEFORM_VIEW_M / 2;
    this.deformCam = new THREE.OrthographicCamera(-hh, hh, hh, -hh, 0, 100);
    this.deformCam.position.set(0, 50, 0);
    this.deformCam.rotation.x = -Math.PI / 2; // straight down; +Y = −Z, the roof's own convention
    this.deformCam.updateMatrixWorld(true);
    this.deformMat = new THREE.MeshBasicNodeMaterial();
    const tex = this.field.map.texture;
    this.deformTexNode = tslTexture(tex);
    // a texel per field sample, centred: u = (x + view/2) / view with the plane centred on the view
    const u = positionWorld.x.add(hh).div(DEFORM_VIEW_M), v = positionWorld.z.add(hh).div(DEFORM_VIEW_M);
    const d = (this.deformTexNode.sample(vec2(u, v) as never) as unknown as { r: ReturnType<typeof float> }).r;
    this.deformMat.colorNode = vec4(d, 0, 0, 1);
    this.deformMat.depthTest = false; this.deformMat.depthWrite = false; this.deformMat.fog = false;
    this.deformMat.name = 'deform-field-bake';
    const plane = new THREE.Mesh(new THREE.PlaneGeometry(DEFORM_VIEW_M, DEFORM_VIEW_M), this.deformMat);
    plane.rotation.x = -Math.PI / 2;
    plane.frustumCulled = false;
    plane.updateMatrixWorld(true);
    this.deformScene.add(plane);
  }

  private tickDeform(dt: number, live: boolean, weather: WeatherLike | undefined): void {
    const on = deformOn() && live;
    deformRuntime.on = on;
    if (!on) { (deformEnabled as unknown as { value: number }).value = 0; return; }
    // a world change forgets the field (nothing persists past the window, or the world)
    const wid = atlas.currentWorldId === null ? null : String(atlas.currentWorldId);
    if (wid !== this.deformWorld) { this.deformWorld = wid; this.field.reset(); }
    this.field.follow(atlas.localX, atlas.localZ);
    // the ride's cushion over snow
    const snow = weather?.snowLevel ?? 0;
    if (atlas.hoverRide) {
      const env = this.app.getModule('Environment') as EnvLike | undefined;
      const g = env?.groundAt?.(atlas.localX, atlas.localZ);
      if (this.lastHover && g !== undefined && Number.isFinite(g)) {
        const [lx, lz] = this.lastHover;
        if (Math.hypot(atlas.localX - lx, atlas.localZ - lz) < 20) {
          const s = hoverStamp(lx, lz, atlas.localX, atlas.localZ, atlas.localY - g, snow);
          if (s) this.field.stamp(s);
        }
      }
      this.lastHover = [atlas.localX, atlas.localZ];
    } else this.lastHover = null;
    // fresh snow fills the tracks; rain settles the ruts (2 Hz — a relax walks every cell)
    this.fillAcc += dt;
    if (this.fillAcc >= 0.5) {
      const pr = weather?.getPrecip?.() ?? { kind: 'none', density: 0 };
      const amt = pr.kind === 'snow' ? snowFillM(this.fillAcc, pr.density) : pr.kind === 'rain' ? rainSettleM(this.fillAcc, rainRateMmH('rain', pr.density)) : 0;
      if (amt > 0) this.field.relax(amt);
      this.fillAcc = 0;
    }
    const nowMs = performance.now();
    const sp = this.app.world.scene.position;
    if (this.field.version !== this.deformBakedVersion && nowMs - this.lastDeformBakeMs > DEFORM_BAKE_MIN_S * 1000) this.bakeDeform();
    if (this.deformBakes > 0) {
      deformBounds.set(this.deformBakedMin[0] + sp.x, this.deformBakedMin[1] + sp.z, DEFORM_VIEW_M, DEFORM_CELL_M);
      (deformEnabled as unknown as { value: number }).value = 1;
    }
  }

  /** Draw the field into the atlas's deformation region. */
  private bakeDeform(): void {
    const renderer = this.app.renderer as unknown as { native: THREE.WebGPURenderer; paused?: boolean };
    if (renderer.paused) return;
    const r = renderer.native;
    this.field.map.upload();
    const rt = this.rt;
    const prevRT = r.getRenderTarget(), prevAuto = r.autoClear;
    const prevVp = rt.viewport.clone();
    try {
      r.setRenderTarget(rt);
      r.autoClear = false;
      rt.viewport.set(...DEFORM_VIEWPORT);
      r.render(this.deformScene, this.deformCam);
    } finally {
      rt.viewport.copy(prevVp);
      r.autoClear = prevAuto;
      r.setRenderTarget(prevRT);
    }
    this.deformBakedVersion = this.field.version;
    this.deformBakedMin = this.field.viewMin();
    this.deformBakes++;
    this.lastDeformBakeMs = performance.now();
  }

  // ── probe helpers ──

  /** Read one texel (ABSOLUTE x, z) of the roof, through the SAME sheared mapping the consumers use (at the player's height). */
  private async sampleAt(x: number, z: number, y = atlas.localY): Promise<{ roof: number; has: number; texel: [number, number] } | null> {
    const sp = this.app.world.scene.position;
    const [minX, minZ, size] = shelterBoundsRender(this.centerAbs[0], this.centerAbs[1], -sp.x, -sp.z, SHELTER_REGION_M);
    const uv = shelterShearedUv(minX, minZ, size, this.shearBaked[0], this.shearBaked[1], this.yRefBaked + sp.y, x + sp.x, y + sp.y, z + sp.z);
    if (!uv) return null;
    const [tu] = shelterTexelUv(uv[0], uv[1]);
    const webgpu = !!(this.app.renderer as unknown as { isWebGPU?: boolean }).isWebGPU;
    const px = Math.min(SHELTER_RES - 1, Math.floor(tu * SHELTER_RES));
    // the row whose map v holds this point, in the backend's own row order (roofRowToMapV inverted)
    const kMap = Math.min(SHELTER_RES - 1, Math.floor(uv[1] * SHELTER_RES));
    let k = 0;
    for (let i = 0; i < SHELTER_RES; i++) if (Math.floor(roofRowToMapV(i, webgpu) * SHELTER_RES) === kMap) { k = i; break; }
    const [rx, ry] = roofReadRect(webgpu);
    const rr = this.app.renderer.native as unknown as { readRenderTargetPixelsAsync: (rt: THREE.RenderTarget, x: number, y: number, w: number, h: number) => Promise<ArrayLike<number>> };
    const buf = await rr.readRenderTargetPixelsAsync(this.rt, rx + px, ry + k, 1, 1);
    const half = buf instanceof Uint16Array;
    const val = (i: number): number => (half ? THREE.DataUtils.fromHalfFloat((buf as Uint16Array)[i]) : buf[i]);
    return { roof: val(0), has: val(3), texel: [px, k] };
  }

  private async exposureAt(x: number, y: number, z: number): Promise<number> {
    const s = await this.sampleAt(x, z, y);
    return s ? shelterExposureCpu(s.has, s.roof, y) : 1;
  }

  /** A grey slab on the roof layer at ABSOLUTE (x, y, z) — probes only (size along x, sizeZ along z). */
  private debugRoof(x: number, z: number, y: number, size: number, sizeZ?: number): THREE.Mesh {
    const m = new THREE.Mesh(new THREE.PlaneGeometry(size, sizeZ ?? size), new THREE.MeshStandardMaterial({ color: 0x3a3a40, roughness: 0.9, side: THREE.DoubleSide }));
    m.rotation.x = -Math.PI / 2;
    m.position.set(x, y, z);
    m.layers.enable(RAIN_SHELTER_LAYER);
    m.name = 'rain-shelter-debug-roof';
    this.app.world.scene.add(m);
    this.debugRoofs.push(m);
    return m;
  }

  private clearDebugRoofs(): number {
    const n = this.debugRoofs.length;
    for (const m of this.debugRoofs.splice(0)) { m.removeFromParent(); m.geometry.dispose(); (m.material as THREE.Material).dispose(); }
    return n;
  }
}
